<template>
  <div class="supervisor-container">
    
    <!-- Bagian Kas Bank -->
    <div class="bank-cash-info section">
      <h2>Kas Operasional Bank</h2>
      <div v-if="bankCash.loading">Memuat...</div>
      <div v-else-if="bankCash.error" class="error-message">{{ bankCash.error }}</div>
      <div v-else class="cash-balance">
        Rp {{ formatCurrency(bankCash.account.balance) }}
      </div>
    </div>
    
    <!-- Bagian 1: Persetujuan Akun PENGGUNA -->
    <div class="section">
      <h1>Persetujuan Akun Pengguna</h1>
      <p>Daftar pengguna baru yang menunggu persetujuan.</p>

      <div v-if="users.loading">Memuat daftar...</div>
      <div v-if="users.error" class="error-message">{{ users.error }}</div>
      <div v-if="!users.loading && users.list.length === 0 && !users.error">
        Tidak ada akun pengguna yang menunggu persetujuan.
      </div>

      <table v-if="!users.loading && users.list.length > 0">
        <thead>
          <tr><th>ID</th><th>Nama</th><th>Email</th><th>Peran</th><th>Aksi</th></tr>
        </thead>
        <tbody>
          <tr v-for="user in users.list" :key="user.id">
            <td>{{ user.id }}</td>
            <td>{{ user.full_name }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.role }}</td>
            <td class="actions">
              <button @click="handleUpdateUserStatus(user.id, 'Active')" class="approve-btn">Approve</button>
              <button @click="handleUpdateUserStatus(user.id, 'Rejected')" class="reject-btn">Reject</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Bagian 2: Persetujuan REKENING -->
    <div class="section">
      <h1>Persetujuan Rekening</h1>
      <p>Daftar rekening baru yang menunggu persetujuan.</p>

      <div v-if="accounts.loading">Memuat daftar...</div>
      <div v-if="accounts.error" class="error-message">{{ accounts.error }}</div>
      <div v-if="!accounts.loading && accounts.list.length === 0 && !accounts.error">
        Tidak ada rekening yang menunggu persetujuan.
      </div>

      <table v-if="!accounts.loading && accounts.list.length > 0">
        <thead>
          <tr><th>No. Rekening</th><th>Nama Pemilik</th><th>Tipe Rekening</th><th>Aksi</th></tr>
        </thead>
        <tbody>
          <tr v-for="acc in accounts.list" :key="acc.id">
            <td>{{ acc.account_number }}</td>
            <td>{{ acc.owner?.full_name || 'Pemilik Tidak Ditemukan' }}</td>
            <td>{{ acc.account_type }}</td>
            <td class="actions">
              <button @click="handleAccountApproval(acc.id, 'Active')" class="approve-btn">Approve</button>
              <button @click="handleAccountApproval(acc.id, 'Rejected')" class="reject-btn">Reject</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Bagian 3: Persetujuan Pinjaman -->
    <div class="section">
      <h1>Persetujuan Pinjaman</h1>
      <p>Daftar pinjaman yang telah diverifikasi dan menunggu persetujuan akhir.</p>

      <div v-if="loans.loading">Memuat daftar...</div>
      <div v-if="loans.error" class="error-message">{{ loans.error }}</div>
      <div v-if="!loans.loading && loans.list.length === 0 && !loans.error">
        Tidak ada pinjaman yang menunggu persetujuan.
      </div>

      <table v-if="!loans.loading && loans.list.length > 0">
        <thead>
          <tr><th>ID Pinjaman</th><th>Nama Nasabah</th><th>Jumlah</th><th>Tujuan</th><th>Aksi</th></tr>
        </thead>
        <tbody>
          <tr v-for="loan in loans.list" :key="loan.id">
            <td>{{ loan.id }}</td>
            <td>{{ loan.owner?.full_name }}</td>
            <td class="amount">Rp {{ formatCurrency(loan.amount) }}</td>
            <td>{{ loan.purpose }}</td>
            <td class="actions">
              <button @click="handleLoanApproval(loan.id, 'Disetujui')" class="approve-btn">Setujui</button>
              <button @click="handleLoanApproval(loan.id, 'Ditolak Akhir')" class="reject-btn">Tolak</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============================================= -->
    <!-- Bagian 4: Persetujuan Transaksi -->
    <!-- ============================================= -->
    <div class="section">
      <h1>Persetujuan Transaksi Teller</h1>
      <p>Daftar transaksi setor/tarik tunai di atas limit yang menunggu persetujuan.</p>

      <div v-if="transactions.loading">Memuat daftar...</div>
      <div v-if="transactions.error" class="error-message">{{ transactions.error }}</div>
      <div v-if="!transactions.loading && transactions.list.length === 0 && !transactions.error">
        Tidak ada transaksi yang menunggu persetujuan.
      </div>

      <table v-if="!transactions.loading && transactions.list.length > 0">
        <thead>
          <tr>
            <th>Tanggal</th>
            <th>Tipe</th>
            <th>No. Rekening</th>
            <th class="amount">Jumlah</th>
            <th>Deskripsi</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tx in transactions.list" :key="tx.id">
            <td>{{ formatDateTime(tx.timestamp) }}</td>
            <td>{{ tx.transaction_code.name }}</td>
            <td>
              <!-- Gunakan optional chaining untuk keamanan -->
              {{ tx.transaction_code.is_debit 
                  ? tx.source_account?.account_number 
                  : tx.destination_account?.account_number 
              }}
            </td>
            <td class="amount" :class="{ 'debit': tx.transaction_code.is_debit, 'credit': tx.transaction_code.is_credit }">
              Rp {{ formatCurrency(tx.amount) }}
            </td>
            <td>{{ tx.description }}</td>
            <td class="actions">
              <button @click="handleTransactionApproval(tx.id)" class="approve-btn">Approve</button>
              <button @click="handleTransactionRejection(tx.id)" class="reject-btn">Reject</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>


  </div>
</template>

<script setup>
import { reactive, onMounted } from 'vue';
import api from '@/services/api';
import { useToast } from 'vue-toastification';
import { formatDateTime } from '@/utils/formatters';

const toast = useToast();

// Gunakan objek reactive terpisah untuk setiap daftar
const users = reactive({ list: [], loading: true, error: null });
const accounts = reactive({ list: [], loading: true, error: null });
const loans = reactive({ list: [], loading: true, error: null });
const bankCash = reactive({ account: null, loading: true, error: null });

const transactions = reactive({ list: [], loading: true, error: null });

// --- Logika untuk Persetujuan Transaksi ---
const fetchPendingTransactions = async () => {
  transactions.loading = true;
  transactions.error = null;
  try {
    const response = await api.getPendingTransactions();
    transactions.list = response.data;
  } catch (err) {
    transactions.error = "Gagal memuat daftar persetujuan transaksi.";
  } finally {
    transactions.loading = false;
  }
};

const handleTransactionApproval = async (txId) => {
  try {
    await api.approveTransaction(txId);
    toast.success(`Transaksi #${txId} berhasil disetujui dan dieksekusi.`);
    await fetchPendingTransactions(); // Refresh daftar
  } catch (err) {
    toast.error(err.response?.data?.detail || "Gagal menyetujui transaksi.");
  }
};


// --- Logika untuk Kas Bank ---
const fetchBankCash = async () => {
  bankCash.loading = true;
  bankCash.error = null;
  try {
    const response = await api.getBankCash();
    bankCash.account = response.data;
  } catch (err) {
    bankCash.error = "Gagal memuat data kas bank.";
  } finally {
    bankCash.loading = false;
  }
};

// --- Logika untuk Persetujuan Akun PENGGUNA ---
const fetchPendingUsers = async () => {
  users.loading = true;
  users.error = null;
  try {
    const response = await api.getPendingUsers();
    users.list = response.data;
  } catch (err) {
    users.error = "Gagal memuat daftar persetujuan akun pengguna.";
  } finally {
    users.loading = false;
  }
};
const handleUpdateUserStatus = async (userId, newStatus) => {
  try {
    await api.updateUserStatus(userId, newStatus);
    toast.success(`Status pengguna #${userId} berhasil diubah.`);
    await fetchPendingUsers();
  } catch (err) {
    toast.error("Gagal mengubah status pengguna.");
  }
};

// --- Logika untuk Persetujuan REKENING ---
const fetchPendingAccounts = async () => {
  accounts.loading = true;
  accounts.error = null;
  try {
    const response = await api.getPendingAccounts();
    accounts.list = response.data;
  } catch (err) {
    accounts.error = "Gagal memuat daftar persetujuan rekening.";
  } finally {
    accounts.loading = false;
  }
};
const handleAccountApproval = async (accountId, newStatus) => {
  try {
    await api.updateAccountStatus(accountId, newStatus);
    toast.success(`Status rekening #${accountId} berhasil diubah.`);
    await fetchPendingAccounts();
  } catch (err) {
    toast.error("Gagal mengubah status rekening.");
  }
};

// --- Logika untuk Persetujuan PINJAMAN ---
const fetchLoansForApproval = async () => {
  loans.loading = true;
  loans.error = null;
  try {
    const response = await api.getLoansForApproval();
    loans.list = response.data;
  } catch (err) {
    loans.error = "Gagal memuat daftar persetujuan pinjaman.";
  } finally {
    loans.loading = false;
  }
};
const handleLoanApproval = async (loanId, newStatus) => {
  try {
    await api.approveLoan(loanId, newStatus);
    toast.success(`Pinjaman #${loanId} berhasil diproses.`);
    await fetchLoansForApproval();
  } catch (err) {
    toast.error("Gagal memproses persetujuan pinjaman.");
  }
};

const handleTransactionRejection = async (txId) => {
  if (!confirm(`Apakah Anda yakin ingin MENOLAK transaksi #${txId}?`)) return;

  try {
    await api.rejectTransaction(txId);
    toast.warning(`Transaksi #${txId} berhasil ditolak.`);
    await fetchPendingTransactions(); // Refresh daftar
  } catch (err) {
    toast.error(err.response?.data?.detail || "Gagal menolak transaksi.");
  }
};

// --- Fungsi Bantuan & Lifecycle ---
const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toLocaleString('id-ID');
};

onMounted(() => {
  fetchBankCash();
  fetchPendingUsers();
  fetchPendingAccounts();
  fetchLoansForApproval();
  fetchPendingTransactions();
});
</script>

<style scoped>
  .section { margin-bottom: 3rem; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  th { background-color: #f2f2f2; }
  .actions button { margin-right: 5px; }
  .approve-btn { background-color: #28a745; color: white; border: none; padding: 5px 10px; cursor: pointer; }
  .reject-btn { background-color: #dc3545; color: white; border: none; padding: 5px 10px; cursor: pointer; }
  .amount { text-align: right; }
.bank-cash-info {
    background-color: #2c3e50;
    color: white;
    padding: 1.5rem;
    border-radius: 8px;
    text-align: center;
  }
  .cash-balance {
    font-size: 2rem;
    font-weight: bold;
    margin-top: 0.5rem;
  }
  .debit { color: #dc3545; }
  .credit { color: #28a745; }
</style>